import { useState, type ReactElement } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  CircularProgress,
  FormGroup,
  IconButton,
  Input,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff, VpnKeyRounded } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useValidateResetToken, type TokenValidity } from '../hooks/useValidateResetToken';
import { useSetPassword } from '../hooks/useSetPassword';
import { PASSWORD_REGEX, PASSWORD_REQUIREMENT } from '../password-policy';
import PasswordStrengthMeter from './PasswordStrengthMeter';

const schema = yup
  .object({
    password: yup
      .string()
      .matches(PASSWORD_REGEX, PASSWORD_REQUIREMENT)
      .required('Password is required'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords do not match')
      .required('Please confirm your password'),
  })
  .required();

type ResetPasswordFormValues = yup.InferType<typeof schema>;

type LinkState = 'valid' | 'expired' | 'used' | 'invalid';

const INVALID_MESSAGES: Record<Exclude<LinkState, 'valid'>, string> = {
  expired: 'This link has expired. Please request a new password reset.',
  used: 'This link has already been used. Please request a new password reset.',
  invalid: 'This link is invalid. Please request a new password reset.',
};

function ResetPasswordForm(): ReactElement {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    data: validity,
    isError: validateTokenHasErrored,
    isInitialLoading,
  } = useValidateResetToken(token);
  const { mutate: setPassword, isPending: isSettingPassword } = useSetPassword(token ?? '');

  const isInvitation = searchParams.get('isInvitation') === 'true';
  const title = isInvitation ? 'Set Your Password' : 'Reset Password';

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({ resolver: yupResolver(schema) });

  const passwordValue = watch('password') ?? '';
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const revealAdornment = (visible: boolean, toggle: () => void): ReactElement => (
    <InputAdornment position="end">
      <IconButton
        aria-label="toggle password visibility"
        onClick={toggle}
        edge="end"
        size="small"
        tabIndex={-1}
      >
        {visible ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  function onSubmit(data: ResetPasswordFormValues): void {
    if (!token || isSettingPassword) return;

    setPassword(
      { password: data.password, confirm_password: data.confirmPassword },
      {
        onSuccess: () => navigate('/login'),
      }
    );
  }

  if (token && isInitialLoading) {
    return (
      <Box className="m-auto flex flex-col items-center">
        <CircularProgress />
        <Typography className="mt-16">Checking your link…</Typography>
      </Box>
    );
  }

  const linkState = getLinkState(validity, validateTokenHasErrored);

  if (linkState && linkState !== 'valid') {
    return (
      <Box className="m-auto w-2/3 max-w-md text-center">
        <Typography variant="h5" component="h1" className="vtwo:mb-4">
          {title}
        </Typography>
        <Typography className="vtwo:mb-4">{INVALID_MESSAGES[linkState]}</Typography>
        <Box className="flex flex-col vtwo:gap-4">
          <Button
            className="w-full"
            variant="contained"
            onClick={() => navigate('/forgot-password')}
          >
            Request new link
          </Button>
          <Button className="w-full" onClick={() => navigate('/login')}>
            Back to login
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="m-auto w-2/3 max-w-md">
      <Typography variant="h5" component="h1" className="text-center vtwo:mb-4">
        {title}
      </Typography>

      <Typography variant="caption" className="vtwo:mb-4 block text-center text-gray-500">
        {PASSWORD_REQUIREMENT}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup className="flex flex-col vtwo:gap-4">
          <div>
            <Input
              className="w-full"
              color="success"
              placeholder={isInvitation ? 'Password' : 'New password'}
              type={showPassword ? 'text' : 'password'}
              {...(errors.password?.message && { error: true })}
              {...register('password')}
              startAdornment={
                <InputAdornment position="start">
                  <VpnKeyRounded />
                </InputAdornment>
              }
              endAdornment={revealAdornment(showPassword, () => setShowPassword((show) => !show))}
            />
            <PasswordStrengthMeter password={passwordValue} />
          </div>
          <div>
            <Input
              className="w-full"
              color="success"
              placeholder={isInvitation ? 'Confirm password' : 'Confirm new password'}
              type={showConfirmPassword ? 'text' : 'password'}
              {...(errors.confirmPassword?.message && { error: true })}
              {...register('confirmPassword')}
              startAdornment={
                <InputAdornment position="start">
                  <VpnKeyRounded />
                </InputAdornment>
              }
              endAdornment={revealAdornment(showConfirmPassword, () =>
                setShowConfirmPassword((show) => !show)
              )}
            />
            {errors.confirmPassword?.message && (
              <Typography variant="caption" color="error">
                {errors.confirmPassword?.message}
              </Typography>
            )}
          </div>

          <Button className="w-full" variant="contained" type="submit" disabled={isSettingPassword}>
            {isSettingPassword && <CircularProgress size={20} className="vtwo:mr-2.5" />}Set
            password
          </Button>
        </FormGroup>
      </form>
    </Box>
  );
}

export default ResetPasswordForm;

function getLinkState(
  validity?: TokenValidity,
  validateTokenHasErrored?: boolean
): LinkState | null {
  if (validateTokenHasErrored) return 'invalid';

  if (!validity) return null;
  if (validity.used) return 'used';
  if (validity.expired) return 'expired';

  return 'valid';
}
